import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useMasterMaps, { selCls, inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { Plus, Ban, FileText, Upload, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { RatingStars, SourceChip, AppStatusBadge } from "../components/PipelineBits";

const SOURCES = ["Referral", "Job Portal", "Walk-in", "Consultant", "Campus", "Internal Transfer", "Rehire"];
const EMPTY = { full_name: "", mobile: "", email: "", current_employer: "", current_designation: "", total_experience_years: "", relevant_experience_years: "", current_ctc: "", expected_ctc: "", notice_period_days: "", preferred_location: "", source: "Job Portal" };

export default function Candidates() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [f, setF] = useState({ search: "", source: "" });
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [dupe, setDupe] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [applyReq, setApplyReq] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (f.search) params.search = f.search;
    if (f.source) params.source = f.source;
    api.get("/candidates", { params })
      .then(({ data }) => { setRows(data.rows); setTotal(data.total); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const save = async (confirm = false) => {
    if (!form.full_name || !form.mobile) { toast.error("Name and mobile are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/candidates", { ...form, confirm_duplicate: confirm });
      toast.success(`${data.full_name} added to the candidate database`);
      setNewOpen(false); setForm(EMPTY); setDupe(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.candidate) setDupe(d);
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/candidates/${c.candidate_id}`);
      setDetail(data); setApplyReq("");
      if (can("pipeline", "write") && reqs.length === 0) {
        const { data: rq } = await api.get("/requisitions", { params: { status: "Approved" } });
        setReqs(rq.slice(0, 100));
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/candidates/${detail.candidate.candidate_id}/resume`, fd);
      toast.success("Resume uploaded");
      openDetail(detail.candidate); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const viewResume = (path) => {
    const token = localStorage.getItem("hire360_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const toggleBlacklist = async () => {
    const c = detail.candidate;
    let reason = null;
    if (!c.blacklist_flag) {
      reason = window.prompt("Blacklist reason (mandatory):");
      if (!reason?.trim()) return;
    }
    try {
      await api.post(`/candidates/${c.candidate_id}/blacklist`, { flag: !c.blacklist_flag, reason });
      toast.success(c.blacklist_flag ? "Removed from blacklist" : "Candidate blacklisted");
      openDetail(c); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addToReq = async () => {
    try {
      const { data } = await api.post("/applications", { candidate_id: detail.candidate.candidate_id, req_id: applyReq });
      toast.success(`Application created${data.other_active_applications ? ` — note: candidate has ${data.other_active_applications} other active application(s)` : ""}`);
      openDetail(detail.candidate);
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div data-testid="candidates-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Candidate Database</h1>
          <p className="text-sm text-[#6B7280] mt-1">Deduplicated talent pool — the same people circulate in this market.</p>
        </div>
        {can("candidates", "write") && (
          <button data-testid="candidate-new-button" onClick={() => { setNewOpen(true); setDupe(null); }}
            className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200 self-start">
            <Plus size={15} /> Add Candidate
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input data-testid="candidate-search" className={`${inputCls} !w-64`} placeholder="Search name / mobile / email…"
          value={f.search} onChange={(e) => setF((p) => ({ ...p, search: e.target.value }))} />
        <select data-testid="candidate-source-filter" className={selCls} value={f.source} onChange={(e) => setF((p) => ({ ...p, source: e.target.value }))}>
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="candidates-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["Candidate", "Contact", "Source", "Exp (yrs)", "CTC cur / exp", "Notice", "Applications", "Resume", ""].map((h, i) => (
                  <th key={i} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#6B7280]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#6B7280]">No candidates</td></tr>
              ) : rows.map((c) => (
                <tr key={c.candidate_id} data-testid={`candidate-row-${c.candidate_id}`} onClick={() => openDetail(c)}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200 cursor-pointer">
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <div className="text-[#0E2240] font-medium flex items-center gap-1.5">
                      {c.full_name}
                      {c.blacklist_flag && <span data-testid={`blacklist-badge-${c.candidate_id}`} className="inline-flex items-center gap-1 text-[9px] uppercase text-white bg-[#BB162B] px-1.5 py-0.5 rounded-sm"><Ban size={9} /> Blacklisted</span>}
                    </div>
                    <div className="text-[10px] text-[#6B7280]">{c.current_designation}{c.current_employer && ` · ${c.current_employer}`}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                    <div className="tabular-nums">{c.mobile}</div>
                    <div className="text-[10px] text-[#6B7280]">{c.email}</div>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><SourceChip source={c.source} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{c.total_experience_years} / {c.relevant_experience_years} rel</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{Number(c.current_ctc).toLocaleString()} / {Number(c.expected_ctc).toLocaleString()}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">{c.notice_period_days}d</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">
                    <span className="text-[#1B7F3B] font-semibold">{c.active_applications}</span>
                    <span className="text-[#6B7280]"> active / {c.total_applications}</span>
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {c.resume_file_url ? (
                      <button onClick={(e) => { e.stopPropagation(); viewResume(c.resume_file_url); }}
                        data-testid={`resume-view-${c.candidate_id}`} className="text-[#1E5AA8] hover:underline text-xs flex items-center gap-1">
                        <FileText size={13} /> View
                      </button>
                    ) : <span className="text-[10px] text-[#6B7280]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-[#1E5AA8]">Open</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 text-xs text-[#6B7280] tabular-nums">{total} candidates</div>
      </div>

      {/* New candidate */}
      <Dialog open={newOpen} onOpenChange={(o) => { setNewOpen(o); if (!o) setDupe(null); }}>
        <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0E2240]">Add Candidate</DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">Duplicates are checked on mobile + email.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <input data-testid="cand-name" className={`${inputCls} col-span-2`} placeholder="Full name *" value={form.full_name} onChange={set("full_name")} />
            <input data-testid="cand-mobile" className={inputCls} placeholder="Mobile *" value={form.mobile} onChange={set("mobile")} />
            <input data-testid="cand-email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
            <input data-testid="cand-employer" className={inputCls} placeholder="Current employer" value={form.current_employer} onChange={set("current_employer")} />
            <input data-testid="cand-desig" className={inputCls} placeholder="Current designation" value={form.current_designation} onChange={set("current_designation")} />
            <input data-testid="cand-exp" type="number" className={inputCls} placeholder="Total exp (yrs)" value={form.total_experience_years} onChange={set("total_experience_years")} />
            <input data-testid="cand-rexp" type="number" className={inputCls} placeholder="Relevant exp (yrs)" value={form.relevant_experience_years} onChange={set("relevant_experience_years")} />
            <input data-testid="cand-cctc" type="number" className={inputCls} placeholder="Current CTC" value={form.current_ctc} onChange={set("current_ctc")} />
            <input data-testid="cand-ectc" type="number" className={inputCls} placeholder="Expected CTC" value={form.expected_ctc} onChange={set("expected_ctc")} />
            <input data-testid="cand-notice" type="number" className={inputCls} placeholder="Notice (days)" value={form.notice_period_days} onChange={set("notice_period_days")} />
            <input data-testid="cand-loc" className={inputCls} placeholder="Preferred location" value={form.preferred_location} onChange={set("preferred_location")} />
            <select data-testid="cand-source" className={`${inputCls} col-span-2`} value={form.source} onChange={set("source")}>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {dupe && (
            <div data-testid="dupe-warning" className="border border-[#BB162B]/40 bg-[#BB162B]/5 rounded-sm p-4 text-xs">
              <div className="font-semibold text-[#BB162B]">{dupe.message}</div>
              {dupe.applications?.length > 0 && (
                <ul className="mt-2 space-y-1 text-[#6B7280]">
                  {dupe.applications.map((a, i) => (
                    <li key={i}>{a.req_no} — {a.desig_name} at {a.branch_name} · {a.current_stage} ({a.status})</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setNewOpen(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
            {dupe ? (
              <button data-testid="cand-save-anyway" disabled={busy} onClick={() => save(true)}
                className="px-4 py-2 text-sm bg-[#BB162B] text-white rounded-sm hover:bg-[#a01324] disabled:opacity-50">Create anyway</button>
            ) : (
              <button data-testid="cand-save" disabled={busy} onClick={() => save(false)}
                className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Save Candidate</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        {detail && (
          <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="candidate-detail-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#0E2240] flex items-center gap-2">
                {detail.candidate.full_name}
                {detail.candidate.blacklist_flag && <span className="text-[10px] uppercase text-white bg-[#BB162B] px-1.5 py-0.5 rounded-sm">Blacklisted</span>}
              </DialogTitle>
              <DialogDescription className="text-xs text-[#6B7280]">
                {detail.candidate.mobile} · {detail.candidate.email || "no email"} · <SourceChip source={detail.candidate.source} />
              </DialogDescription>
            </DialogHeader>
            {detail.candidate.blacklist_flag && (
              <div className="text-xs text-[#BB162B] border border-[#BB162B]/30 bg-[#BB162B]/5 rounded-sm p-3">Reason: {detail.candidate.blacklist_reason}</div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              {[["Experience", `${detail.candidate.total_experience_years}y (${detail.candidate.relevant_experience_years}y rel)`],
                ["Current CTC", Number(detail.candidate.current_ctc).toLocaleString()],
                ["Expected CTC", Number(detail.candidate.expected_ctc).toLocaleString()],
                ["Notice", `${detail.candidate.notice_period_days} days`]].map(([l, v]) => (
                <div key={l} className="border border-gray-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase text-[#6B7280]">{l}</div>
                  <div className="text-[#0E2240] tabular-nums mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">Applications</div>
              {detail.applications.length === 0 ? (
                <div className="text-xs text-[#6B7280]">No applications yet.</div>
              ) : detail.applications.map((a) => (
                <div key={a.application_id} className="flex items-center justify-between border border-gray-200 rounded-sm p-2.5 mb-1.5 text-xs">
                  <div>
                    <span className="font-medium text-[#0E2240]">{a.req_no}</span> — {a.desig_name} at {a.branch_name}
                    <div className="text-[10px] text-[#6B7280]">{a.current_stage} · <RatingStars value={a.overall_rating} /></div>
                  </div>
                  <AppStatusBadge status={a.status} />
                </div>
              ))}
            </div>
            {can("pipeline", "write") && !detail.candidate.blacklist_flag && (
              <div className="flex items-center gap-2">
                <select data-testid="apply-req-select" className={`${selCls} flex-1`} value={applyReq} onChange={(e) => setApplyReq(e.target.value)}>
                  <option value="">— Add to approved requisition —</option>
                  {reqs.map((r) => <option key={r.req_id} value={r.req_id}>{r.req_no} · {r.desig_name} · {r.branch_name}</option>)}
                </select>
                <button data-testid="apply-req-btn" disabled={!applyReq} onClick={addToReq}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">
                  <UserPlus size={13} /> Apply
                </button>
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              {can("candidates", "write") && (
                <>
                  <label className="flex items-center gap-1.5 px-3 py-2 text-xs border border-gray-300 rounded-sm hover:bg-gray-50 cursor-pointer">
                    <Upload size={13} /> {detail.candidate.resume_file_url ? "Replace resume" : "Upload resume"}
                    <input data-testid="resume-upload-input" type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={uploadResume} />
                  </label>
                  <button data-testid="blacklist-toggle" onClick={toggleBlacklist}
                    className={`px-3 py-2 text-xs rounded-sm border ${detail.candidate.blacklist_flag ? "border-gray-300 text-[#6B7280] hover:bg-gray-50" : "border-[#BB162B]/60 text-[#BB162B] hover:bg-[#BB162B]/5"}`}>
                    {detail.candidate.blacklist_flag ? "Remove from blacklist" : "Blacklist candidate"}
                  </button>
                </>
              )}
              {detail.candidate.resume_file_url && (
                <button onClick={() => viewResume(detail.candidate.resume_file_url)} className="px-3 py-2 text-xs text-[#1E5AA8] border border-[#1E5AA8]/40 rounded-sm hover:bg-[#1E5AA8]/5">View resume</button>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
